import ApplicationController from './application_controller'

/**
 * HeaderCartController
 *
 * Manages the shopping cart icon in the header with item count badge.
 * Listens for cartUpdated events to update the badge count dynamically.
 * Tracks cart context (opportunity/giving day) for DoorDash-style context switching.
 */
export default class extends ApplicationController {
  static targets = [
    'badge',             // Badge showing item count
    'container',         // Main cart icon container
    'icon',              // The cart icon itself
    'contextIndicator'   // Shows current cart context name
  ]

  static values = {
    checkoutUrl: String,    // URL to navigate to checkout
    itemCount: Number,
    contextName: String,    // Name of current cart context (opportunity/giving day)
    contextType: String,    // Type: 'opportunity', 'giving_day', 'organization'
    contextId: Number,      // ID of the context
    total: Number           // Cart total for display
  }

  connect() {
    super.connect()
    // Set defaults for Stimulus v2
    if (!this.hasItemCountValue) this.itemCountValue = 0

    // Listen for cart update events from opportunity_cart_controller
    this.handleCartUpdate = this.handleCartUpdate.bind(this)
    document.addEventListener('cartUpdated', this.handleCartUpdate)

    // Listen for context switch events
    this.handleContextSwitch = this.handleContextSwitch.bind(this)
    document.addEventListener('cart:context-switched', this.handleContextSwitch)

    // Initial UI update
    this.updateUI()
  }

  disconnect() {
    document.removeEventListener('cartUpdated', this.handleCartUpdate)
    document.removeEventListener('cart:context-switched', this.handleContextSwitch)
  }

  /**
   * Handle cart updated events
   */
  handleCartUpdate(event) {
    const { cart } = event.detail

    if (cart && typeof cart.item_count !== 'undefined') {
      this.itemCountValue = cart.item_count
      this.totalValue = cart.total || 0

      // Update context info if provided
      if (cart.context) {
        this.contextTypeValue = cart.context.type || ''
        this.contextIdValue = cart.context.id || ''
        this.contextNameValue = cart.context.name || ''
      }

      this.updateUI()
      this.animateBadge()
    }
  }

  /**
   * Handle context switch events (when cart is cleared for new context)
   */
  handleContextSwitch(event) {
    // Refresh cart data from server after context switch
    this.refreshCart()
  }

  /**
   * Update the UI based on item count and context
   */
  updateUI() {
    const count = this.itemCountValue
    const contextName = this.contextNameValue

    if (this.hasBadgeTarget) {
      // Update badge text
      this.badgeTarget.textContent = count

      // Show/hide badge based on count
      if (count > 0) {
        this.badgeTarget.classList.remove('d-none')
      } else {
        this.badgeTarget.classList.add('d-none')
      }
    }

    // Update context indicator if present
    if (this.hasContextIndicatorTarget) {
      if (count > 0 && contextName) {
        this.contextIndicatorTarget.textContent = this.truncateText(contextName, 15)
        this.contextIndicatorTarget.classList.remove('d-none')
      } else {
        this.contextIndicatorTarget.classList.add('d-none')
      }
    }

    // Update container tooltip
    if (this.hasContainerTarget) {
      if (contextName) {
        this.containerTarget.title = `Cart: ${contextName}`
      } else {
        this.containerTarget.title = 'Shopping Cart'
      }
    }
  }

  /**
   * Truncate text to specified length
   */
  truncateText(text, length) {
    if (text.length <= length) return text
    return text.substring(0, length - 1) + '…'
  }

  /**
   * Animate badge on update (pulse effect)
   */
  animateBadge() {
    if (this.hasBadgeTarget && this.itemCountValue > 0) {
      this.badgeTarget.classList.add('cart-badge-pulse')
      setTimeout(() => {
        this.badgeTarget.classList.remove('cart-badge-pulse')
      }, 300)
    }
  }

  /**
   * Navigate to checkout when cart icon is clicked
   */
  navigateToCheckout(event) {
    // Allow default link behavior, but could add logic here
    // e.g., prevent navigation if cart is empty
    if (this.itemCountValue === 0) {
      event.preventDefault()
      // Optionally show a message that cart is empty
    }
  }

  /**
   * Refresh cart data from server (for page load or manual refresh)
   */
  refreshCart() {
    fetch('/shopping_cart/totals', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success && data.cart) {
        this.itemCountValue = data.cart.item_count || 0
        this.updateUI()
      }
    })
    .catch(error => {
      console.error('Error fetching cart:', error)
    })
  }
}
